import { useState, useEffect } from "react";
import * as Sentry from "@sentry/react";

import {
  Box,
  Grid,
  Typography,
  StyledEngineProvider,
  // InputAdornment,
  ThemeProvider,
} from "@mui/material";
// import AttachMoneyIcon from "@mui/icons-material/AttachMoney";

import { tonsToLbs } from "@aclymatepackages/converters";
//eslint-disable-next-line
import { TextField, AnimatedLogo } from "@aclymatepackages/atoms/";
import { useLayoutHelpers } from "@aclymatepackages/themes";
// import { numbersRegExpTest, emailRegExpTest } from "@aclymatepackages/reg-exp";
import { mergeDarkTheme, mainTheme } from "@aclymatepackages/themes";

import OffsetsPurchaseFlow from "./OffsetsPurchaseFlow";
// import PurchaseInputSection from "./PurchaseInputSection";
import MyAclymateCta from "./MyAclymateCta";

// import { lastYearsFestivarianTravelDataSummary } from "../../helpers/components/events";
import { fetchOurApi } from "../../helpers/utils/apiCalls";
import { analyticsTrack } from "../../helpers/analytics";

const usePaymentIntent = () => {
  const [stripeData, setStripeData] = useState(null);
  const [stripeDataLoading, setStripeDataLoading] = useState(true);

  const setPaymentIntent = ({
    tonsToPurchase,
    portfolioId,
    stripeCustomerId,
    eventName,
    portfolioName,
    stripeChargeId,
  }) => {
    if (!stripeChargeId) {
      return fetchOurApi({
        path: "/stripe/create-event-purchase-intent",
        method: "POST",
        data: {
          portfolioId,
          tonsToPurchase,
          stripeCustomerId,
          description: `${eventName}- ${portfolioName}`,
        },
        callback: (stripeData) => {
          setStripeDataLoading(false);
          return setStripeData(stripeData);
        },
      });
    }

    setStripeDataLoading(false);
    return fetchOurApi({
      path: "/stripe/update-payment-intent",
      method: "POST",
      data: { portfolioId, tonsToPurchase, stripeChargeId },
    });
  };

  return { stripeData, stripeDataLoading, setPaymentIntent };
};

// const EmailInput = ({
//   editServerData,
//   individual,
//   event,
//   type,
//   ...otherProps
// }) => {
//   const { id: eventId } = event;
//   const { id: individualId } = individual;
//   const [email, setEmail] = useState("");
//   const [emailError, setEmailError] = useState("");

//   const onNextStep = async () => {
//     const { error } = await fetchOurApi({
//       path: "/events/update-partner-email",
//       method: "POST",
//       data: { email, partnerId: individualId, eventId, type },
//       callback: (res) => res,
//     });

//     if (error) {
//       return setEmailError("An error occurred with your email");
//     }

//     return editServerData("attendee")({ ...individual, email });
//   };

//   return (
//     <PurchaseInputSection
//       title="Tell us where to send your receipt"
//       input={
//         <TextField
//           label="Enter your email address"
//           value={email}
//           setValue={setEmail}
//           helperText={emailError}
//           error={!!emailError}
//         />
//       }
//       onNextStep={onNextStep}
//       nextStepDisabled={!emailRegExpTest(email)}
//       {...otherProps}
//     />
//   );
// };

// const ProjectVolumeSelection = ({
//   purchaseDescription,
//   displayCost,
//   totalEventLbs,
//   project,
//   groupSize,
//   purchaseDollars,
//   lbsToPurchase,
//   setLbsToPurchase,
//   setPurchaseDollars,
//   setTypeOfPurchase,
//   typeOfPurchase,
//   setStripeData,
//   stripeChargeId,
//   negativeFootprint,
//   stripeCustomerId,
//   setPaymentIntent,
//   ...otherProps
// }) => {
//   const { totalThousandLbsCost } = project || {};

//   const [stripeDataLoading, setStripeDataLoading] = useState(false);

//   const isNextStepDisabled = () =>
//     stripeDataLoading ||
//     Number(purchaseDollars) < 1 ||
//     !numbersRegExpTest(purchaseDollars);

//   const onNextStep = () => {
//     const lbsToPurchase = (purchaseDollars / totalThousandLbsCost) * 1000;

//     setLbsToPurchase(lbsToPurchase);
//     return setPaymentIntent({ lbsToPurchase, purchaseDollars });
//   };

//   return (
//     <PurchaseInputSection
//       title="How much of an impact would you like to make?"
//       input={
//         <TextField
//           label="Select how much you want to pay for offsets"
//           value={purchaseDollars || ""}
//           setValue={setPurchaseDollars}
//           InputProps={{
//             startAdornment: (
//               <InputAdornment position="start">
//                 <AttachMoneyIcon />
//               </InputAdornment>
//             ),
//           }}
//           error={
//             purchaseDollars &&
//             (purchaseDollars < 1 || !numbersRegExpTest(purchaseDollars))
//           }
//           helperText={
//             !!purchaseDollars &&
//             purchaseDollars < 1 &&
//             "You must purchase at least $1 of offsets"
//           }
//         />
//       }
//       nextStepDisabled={isNextStepDisabled()}
//       onNextStep={onNextStep}
//       {...otherProps}
//     />
//   );
// };

const PurchaseThanks = ({ thanksImage }) => {
  const { isMedium } = useLayoutHelpers();

  return (
    <Box style={{ height: "100%", overflowX: "hidden", overflowY: "auto" }}>
      <Grid
        container
        direction="column"
        spacing={4}
        alignItems="center"
        wrap="nowrap"
      >
        <Grid item sm={6}>
          <img
            src={thanksImage || "/images/purchase-thanks.svg"}
            style={{ width: "100%" }}
            alt="Thanks for purchasing offsets from Aclymate"
          />
        </Grid>
        <Grid item>
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={isMedium ? mainTheme : mergeDarkTheme}>
              <Typography variant="h4" align="center" color="textPrimary">
                The Planet thanks you for your contribution
              </Typography>
              <Typography
                variant="subtitle1"
                align="center"
                color="textSecondary"
              >
                You should have received an email to confirm that your offsets
                have been retired.
              </Typography>
            </ThemeProvider>
          </StyledEngineProvider>
        </Grid>
        <Grid item sm={11}>
          <MyAclymateCta />
        </Grid>
      </Grid>
    </Box>
  );
};

const EventOffsetPurchase = ({
  portfolio,
  event,
  editServerData,
  attendee: individual,
  purchaseVolumeTonsOptions = [1],
  subaccountId,
}) => {
  const { setPaymentIntent, stripeData, stripeDataLoading } =
    usePaymentIntent();

  const { name: eventName } = event || {};
  const { stripeCustomerId, name, email, isEmailOptIn } = individual || {};
  //eslint-disable-next-line
  const { slug, displayCost, name: projectName, id } = portfolio || {};

  const [tonsToPurchase, setTonsToPurchase] = useState(0);

  useEffect(() => {
    if (
      purchaseVolumeTonsOptions.length === 1 &&
      !stripeData &&
      stripeDataLoading &&
      !tonsToPurchase
    ) {
      const [tonsToPurchase] = purchaseVolumeTonsOptions;
      setTonsToPurchase(tonsToPurchase);
      setPaymentIntent({
        tonsToPurchase,
        portfolioId: id,
        stripeCustomerId,
        eventName,
        portfolioName: projectName,
      });
    }
  }, [
    eventName,
    id,
    projectName,
    purchaseVolumeTonsOptions,
    setPaymentIntent,
    stripeCustomerId,
    stripeData,
    stripeDataLoading,
    tonsToPurchase,
  ]);

  ///ALL OF THIS FUNCTIONALITY IS DISABLE FOR NOW
  //In the future we'll have the option to handle multiple purchase volume options and we'll rebuild this
  // const emailInputRow = email
  //   ? []
  //   : [
  //       {
  //         Component: EmailInput,
  //         props: { editServerData, individual, event, type: surveyType },
  //       },
  //     ];

  // const buildDisplayCost = () => {
  //   if (negativeFootprint || displayCost < 1) {
  //     return 1;
  //   }

  //   return displayCost;
  // };

  // const purchaseRows = [
  //   {
  //     Component: ProjectVolumeSelection,
  //     props: {
  //       purchaseVolumeTonsOptions,
  //       purchaseDescription: `${eventName}- ${projectName}`,
  //       totalEventLbs: tonsToLbs(totalEventTonsCo2e),
  //       displayCost: buildDisplayCost(),
  //       project,
  //       purchaseDollars,
  //       lbsToPurchase,
  //       setLbsToPurchase,
  //       setPurchaseDollars,
  //       setTypeOfPurchase,
  //       typeOfPurchase,
  //       setStripeData,
  //       groupSize,
  //       stripeChargeId,
  //       negativeFootprint,
  //       stripeCustomerId,
  //     },
  //   },
  //   ...emailInputRow,
  // ];

  const purchaseRows = [];

  // const groupOffsetPurchaseDataObj =
  //   negativeFootprint || surveyType !== "attendee"
  //     ? {}
  //     : {
  //         groupOffsetPurchaseData: {
  //           groupSize,
  //           typeOfPurchase,
  //         },
  //       };

  const purchaseCallback = async () => {
    const { stripeChargeId, paymentIntentDollars: purchaseDollars } =
      stripeData;

    const fetchData = {
      tonsToPurchase,
      stripeChargeId,
      event,
      individual,
      portfolio,
      subaccountId,
      // ...groupOffsetPurchaseDataObj,
    };

    const callbackFunction = (res) => {
      const { success, error, offset } = res;

      if (error) {
        Sentry.setContext("Event Individual", {
          id: individual?.id,
          email: individual?.email,
          stripeCustomerId: individual?.stripeCustomerId,
        });
        Sentry.setContext("Stripe Data", {
          stripeChargeId: stripeData?.stripeChargeId,
          paymentIntentDollars: stripeData?.paymentIntentDollars,
        });
        Sentry.setContext("Purchase data", {
          projectSlug: slug,
          tonsToPurchase,
          purchaseDollars,
        });
        Sentry.captureException(error);
      }

      if (success) {
        editServerData("offsets")([offset]);
      }

      if (isEmailOptIn) {
        analyticsTrack("Event Offsets Purchase", {
          eventName,
          name,
          email,
          purchaseDollars,
        });
      }

      return res;
    };

    // if (surveyType === "attendee")
    return await fetchOurApi({
      path: "/individuals/event-offset-purchase",
      method: "POST",
      data: fetchData,
      callback: callbackFunction,
    });
    // }

    //TODO: this hasn't been updated and won't work yet
    //We can update it next time we make a partner survey (if that ever happens)
    // return await fetchOurApi({
    //   path: "/events/partner-offset-purchase",
    //   method: "POST",
    //   data: fetchData,
    //   callback: callbackFunction,
    // });
  };

  return (
    <>
      {stripeDataLoading ? (
        <AnimatedLogo />
      ) : (
        <OffsetsPurchaseFlow
          project={portfolio}
          thanksStep={<PurchaseThanks {...event} />}
          dataEntryRows={purchaseRows}
          stripeData={stripeData}
          purchaseCallback={purchaseCallback}
          inputData={{
            lbsToPurchase: tonsToLbs(tonsToPurchase),
            purchaseDollars: stripeData?.paymentIntentDollars / 100 || 0,
          }}
          noFooter
          noVolume
          title="Purchase this portfolio"
        />
      )}
    </>
  );
};
export default EventOffsetPurchase;
